import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  KeyRound,
  Layers,
  Loader2,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { accessService, type Permission } from "@/services/accessService";
import { useUiStore } from "@/store/uiStore";

const ACTION_STYLES: Record<string, string> = {
  assign: "bg-blue-50 text-blue-700 ring-blue-100",
  create: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  delete: "bg-rose-50 text-rose-700 ring-rose-100",
  update: "bg-amber-50 text-amber-800 ring-amber-100",
  manage: "bg-violet-50 text-violet-700 ring-violet-100",
  view: "bg-sky-50 text-sky-700 ring-sky-100",
  start: "bg-cyan-50 text-cyan-700 ring-cyan-100",
  use: "bg-indigo-50 text-indigo-700 ring-indigo-100",
};

function actionChipClass(action: string) {
  return (
    ACTION_STYLES[action.toLowerCase()] ??
    "bg-slate-100 text-slate-600 ring-slate-200"
  );
}

function humanizeResource(resource: string) {
  return resource
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function PermissionsTab() {
  const [perms, setPerms] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const { startPageLoading, stopPageLoading } = useUiStore();
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  useEffect(() => {
    accessService.listPermissions().then(setPerms).finally(() => setLoading(false));
  }, []);

  const actions = useMemo(() => {
    const set = new Set(perms.map((p) => p.action.toLowerCase()));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [perms]);

  const resourcesCount = useMemo(
    () => new Set(perms.map((p) => p.resource)).size,
    [perms],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return perms.filter((p) => {
      if (actionFilter !== "all" && p.action.toLowerCase() !== actionFilter) {
        return false;
      }
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.resource.toLowerCase().includes(q) ||
        p.action.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [perms, search, actionFilter]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, Permission[]>>((acc, p) => {
      (acc[p.resource] ||= []).push(p);
      return acc;
    }, {});
  }, [filtered]);

  const sortedResources = useMemo(
    () => Object.keys(grouped).sort((a, b) => a.localeCompare(b)),
    [grouped],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-[#009BF5]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:grid-cols-3">
        <StatCell
          icon={<KeyRound size={16} />}
          label="Permissions"
          value={perms.length}
        />
        <StatCell
          icon={<Layers size={16} />}
          label="Resources"
          value={resourcesCount}
          bordered
        />
        <StatCell
          icon={<ShieldCheck size={16} />}
          label="Actions"
          value={actions.length}
          bordered
        />
      </div>

      {/* Search + action filters */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:gap-3 sm:px-4 sm:py-3">
        <div className="relative w-full shrink-0 sm:w-[220px] lg:w-[260px]">
          <Search
            size={15}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            id="permissions-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions..."
            className="h-10 w-full rounded-full border border-slate-200 bg-slate-50/80 pl-10 pr-4 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all focus:border-[#009BF5]/40 focus:bg-white focus:ring-2 focus:ring-[#009BF5]/15"
          />
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-0.5">
          <ActionFilterChip
            label="All"
            active={actionFilter === "all"}
            onClick={() => setActionFilter("all")}
          />
          {actions.map((action) => (
            <ActionFilterChip
              key={action}
              label={action}
              active={actionFilter === action}
              onClick={() => setActionFilter(action)}
            />
          ))}
        </div>

        <div className="shrink-0 text-right text-xs font-medium tabular-nums text-slate-400 sm:min-w-[4.5rem]">
          {filtered.length} of {perms.length}
        </div>
      </div>

      <p className="text-xs text-slate-400 italic">
        System permissions are read-only. Custom permissions can be removed when unused.
      </p>

      {/* Resource cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {sortedResources.map((resource) => {
          const items = grouped[resource];
          return (
            <section
              key={resource}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                <h2 className="text-[15px] font-semibold text-slate-900">
                  {humanizeResource(resource)}
                </h2>
                <span className="text-xs font-medium text-slate-400">
                  {resource} · {items.length}
                </span>
              </div>

              <ul className="divide-y divide-slate-100">
                {items
                  .slice()
                  .sort((a, b) => a.action.localeCompare(b.action))
                  .map((p) => (
                    <li
                      key={p.id}
                      className="group flex items-start gap-3 px-5 py-3.5 transition-colors hover:bg-slate-50/70"
                    >
                      <span
                        className={`mt-0.5 inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ring-1 ring-inset ${actionChipClass(p.action)}`}
                      >
                        {p.action}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="break-all font-mono text-[13px] font-semibold text-slate-800">
                          {p.name}
                        </div>
                        {p.description ? (
                          <p className="mt-0.5 text-[13px] leading-snug text-slate-500">
                            {p.description}
                          </p>
                        ) : null}
                      </div>

                      <div className="shrink-0 pt-0.5">
                        {p.isSystem ? (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-300">
                            system
                          </span>
                        ) : (
                          <button
                            id={`delete-permission-${p.id}`}
                            title={`Delete ${p.name}`}
                            onClick={() => {
                              if (!confirm(`Delete permission "${p.name}"?`)) return;
                              accessService.deletePermission(p.id).then(() =>
                                setPerms((prev) => prev.filter((x) => x.id !== p.id)),
                              );
                            }}
                            className="rounded-md p-1.5 text-slate-300 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </section>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-medium text-slate-600">No permissions match your search.</p>
          <p className="mt-1 text-xs text-slate-400">Try a different keyword or clear the action filter.</p>
        </div>
      )}
    </div>
  );
}

function StatCell({
  icon,
  label,
  value,
  bordered,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  bordered?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3 px-5 py-4 ${
        bordered ? "border-l border-slate-100" : ""
      }`}
    >
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
        {icon}
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          {label}
        </div>
        <div className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-slate-900">
          {value}
        </div>
      </div>
    </div>
  );
}

function ActionFilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors",
        active
          ? "bg-bridge-accent text-white shadow-sm"
          : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
